'use client';

import React, { forwardRef } from 'react';
import { Icons } from '@shared/ui';
import clsx from 'clsx';
import { useBuyerProductStore } from 'app/store/product';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import STYLE from './Breadcrumb.module.scss';
import { BreadcrumbItem } from './BreadcrumbItem';
import { getBreadcrumbList, defaultBreadcrumb } from './utils';
import { type BreadcrumbItemPropType } from './BreadcrumbItem';

interface BreadcrumbPropType extends BasicShopUIPropTypes, BuyerLayoutProps {
    customBreadcrumbList?: Array<{
        href: string;
        name: string;
    }>;
}

export const Breadcrumb = forwardRef<HTMLDivElement, BreadcrumbPropType>((props, ref) => {
    const { isEditingMode, isBuyer, style, customBreadcrumbList } = props;

    const { product } = useBuyerProductStore();

    let breadcrumbList: Array<BreadcrumbItemPropType> = [];

    if (customBreadcrumbList) {
        breadcrumbList = customBreadcrumbList;
    } else {
        breadcrumbList =
            isBuyer && product ? getBreadcrumbList(product) : defaultBreadcrumb;
    }

    if (!breadcrumbList || !breadcrumbList.length) {
        return null;
    }

    return (
        <nav
            ref={ref}
            style={style}
            className={clsx(
                STYLE.breadcrumb,
                isEditingMode && !isBuyer && STYLE.isEditing
            )}
            aria-label="breadcrumb"
        >
            <ol className={STYLE.breadcrumbList}>
                {breadcrumbList.map((item, index) => (
                    <React.Fragment key={`breadcrumb-${index}`}>
                        {index !== 0 && <Icons.csr.CaretRight size="1em" weight="bold" />}
                        {item.nodes ? (
                            // 處理多節點情況
                            item.nodes.map((category, subIndex) => (
                                <React.Fragment
                                    key={`sub-breadcrumb-${index}-${subIndex}`}
                                >
                                    {subIndex !== 0 && (
                                        <span className={STYLE.separator}>/</span>
                                    )}
                                    <BreadcrumbItem
                                        href={category.href}
                                        name={category.name}
                                        isCurrentPage={false}
                                    />
                                </React.Fragment>
                            ))
                        ) : (
                            // 處理單節點情況
                            <BreadcrumbItem
                                href={item.href}
                                name={item.name}
                                isCurrentPage={index === breadcrumbList.length - 1}
                            />
                        )}
                    </React.Fragment>
                ))}
            </ol>
        </nav>
    );
});

Breadcrumb.displayName = 'Breadcrumb';
